import { t } from "@lingui/core/macro";
import { GetAppointmentLog } from "@/server-types";

export const generateLogTitle = (
  log: GetAppointmentLog["response"]["logs"][0],
) => {
  if (log.table === "appointment") {
    if (log.action === "update") {
      return log.old_data.startTimeUtc !== log.new_data.startTimeUtc
        ? t`Termin prenaročen`
        : t`Termin posodobljen`;
    }
    if (log.action === "insert") return t`Termin ustvarjen`;
    if (log.action === "delete") return t`Termin izbrisan`;
  } else if (log.table === "user_appointment") {
    if (log.action === "issue_invoice") return t`Račun izdan`;
    if (log.action === "insert") return t`Stranka dodana`;
    if (log.action === "delete") return t`Stranka odstranjena`;
    if (log.action === "update") return t`Termin posodobljen`;
  } else if (log.table === "AppointmentResource") {
    if (log.action === "insert") return t`Sredstvo dodano`;
    if (log.action === "update") return t`Sredstva spremenjena`;
    if (log.action === "delete") return t`Sredstva odstranjena`;
  } else if (log.table === "AppointmentUser") {
    if (log.action === "insert") return t`Zaposleni dodan`;
    if (log.action === "update") return t`Zaposleni spremenjen`;
    if (log.action === "delete") return t`Zaposleni odstranjen`;
  }
  return t`Termin spremenjen`;
};

export const generateDescription = (
  log: GetAppointmentLog["response"]["logs"][0],
  options?: {
    includeContactCustomer?: boolean;
    notificationSettingLabelById?: Record<number, string>;
  },
) => {
  const includeContactCustomer = options?.includeContactCustomer ?? true;
  const changes: string[] = [];
  if (log.table === "appointment" && log.action === "insert") {
    changes.push(
      t`Termin je bil dodan za storitev ${log.new_data.serviceNameLocalized} dne ${log.new_data.startTimeLocal}. Trajanje termina je ${log.new_data.duration} minut`,
    );

    if (log.new_data.blockBeforeMins > 0) {
      changes.push(
        t`Blokiranje pred terminom je ${log.new_data.blockBeforeMins} minut`,
      );
    }
    if (log.new_data.blockAfterMins > 0) {
      changes.push(
        t`Blokiranje po terminu je ${log.new_data.blockAfterMins} minut`,
      );
    }
  }
  if (log.table === "appointment" && log.action === "update") {
    if (log.old_data.startTimeLocal !== log.new_data.startTimeLocal) {
      changes.push(
        t`Začetni čas storitve ${log.new_data.serviceNameLocalized} je bil spremenjen iz ${log.old_data.startTimeLocal} na ${log.new_data.startTimeLocal}`,
      );
    }
    if (
      log.old_data.serviceNameLocalized !== log.new_data.serviceNameLocalized
    ) {
      changes.push(
        t`Storitev je bila spremenjena iz ${log.old_data.serviceNameLocalized} na ${log.new_data.serviceNameLocalized}`,
      );
    }
    if (log.old_data.duration !== log.new_data.duration) {
      changes.push(
        t`Trajanje storitve ${log.new_data.serviceNameLocalized} je bilo spremenjeno iz ${log.old_data.duration} minut na ${log.new_data.duration} minut`,
      );
    }
    if (log.old_data.blockBeforeMins !== log.new_data.blockBeforeMins) {
      changes.push(
        t`Blokiranje pred terminom je bilo spremenjeno iz ${log.old_data.blockBeforeMins} minut na ${log.new_data.blockBeforeMins} minut`,
      );
    }
    if (log.old_data.blockAfterMins !== log.new_data.blockAfterMins) {
      changes.push(
        t`Blokiranje po terminu je bilo spremenjeno iz ${log.old_data.blockAfterMins} minut na ${log.new_data.blockAfterMins} minut`,
      );
    }
  }

  if (log.table === "user_appointment") {
    if (log.action === "issue_invoice") {
      if (log.customer) {
        return t`Račun je bil izdan za stranko ${log.customer.name} ${log.customer.lastName}.`;
      }
      return t`Račun je bil izdan za termin.`;
    }
    if (log.action === "insert") {
      changes.push(
        t`Stranka ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bila dodana na termin.`,
      );

      if (log.new_data.customerSubscriptionId != null) {
        const customerSubscriptionLabel = formatCustomerSubscriptionLabel(
          log.new_data.customerSubscriptionId,
          log.new_data.customerSubscriptionName,
        );

        changes.push(
          t`Na terminu za stranko ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bila označena uporaba paketa ${customerSubscriptionLabel}`,
        );
      }

      if (includeContactCustomer && log.new_data.contactCustomer) {
        changes.push(
          t`Kontaktna stranka za termin je ${log.new_data.contactCustomer.name} ${log.new_data.contactCustomer.lastName}.`,
        );
      }
      return changes.join(" ");
    }
    if (log.action === "delete") {
      if (log.new_data.customer) {
        changes.push(
          t`Stranka ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bila odstranjena s termina`,
        );
      }
      if (log.old_data.paymentStatus !== log.new_data.paymentStatus) {
        changes.push(
          t`Status plačila termina je bil spremenjen iz "${getPaymentStatusText(log.old_data.paymentStatus)}" na "${getPaymentStatusText(log.new_data.paymentStatus)}"`,
        );
      }
    }
    if (log.action === "update") {
      const oldAttendance = log.old_data.customerDidAttend;
      const newAttendance = log.new_data.customerDidAttend;

      if (
        oldAttendance != null &&
        newAttendance != null &&
        oldAttendance !== newAttendance
      ) {
        changes.push(
          t`Prihod stranke ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bil spremenjen iz "${getAttendanceStatusText(oldAttendance)}" na "${getAttendanceStatusText(newAttendance)}"`,
        );
      }

      if (log.old_data.price !== log.new_data.price) {
        changes.push(
          t`Cena termina je bila spremenjena iz ${log.old_data.price} na ${log.new_data.price}`,
        );
      }
      const oldComment = normalizeAppointmentComment(log.old_data.comment);
      const newComment = normalizeAppointmentComment(log.new_data.comment);

      if (oldComment !== newComment) {
        changes.push(
          t`Komentar termina za stranko ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bil spremenjen na "${newComment}"`,
        );
      }
      if (log.old_data.paymentStatus !== log.new_data.paymentStatus) {
        changes.push(
          t`Status plačila termina je bil spremenjen iz "${getPaymentStatusText(log.old_data.paymentStatus)}" na "${getPaymentStatusText(log.new_data.paymentStatus)}"`,
        );
      }

      if (log.old_data.FK_customerId !== log.new_data.FK_customerId) {
        changes.push(
          t`Stranka je bila spremenjena iz ${log.old_data.customer.name} ${log.old_data.customer.lastName} na ${log.new_data.customer.name} ${log.new_data.customer.lastName}`,
        );
      }

      if (
        log.old_data.customerSubscriptionId == null &&
        log.new_data.customerSubscriptionId != null
      ) {
        const newCustomerSubscriptionLabel = formatCustomerSubscriptionLabel(
          log.new_data.customerSubscriptionId,
          log.new_data.customerSubscriptionName,
        );

        changes.push(
          t`Na terminu za stranko ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bila označena uporaba paketa ${newCustomerSubscriptionLabel}`,
        );
      }

      if (
        log.old_data.customerSubscriptionId != null &&
        log.new_data.customerSubscriptionId == null
      ) {
        const oldCustomerSubscriptionLabel = formatCustomerSubscriptionLabel(
          log.old_data.customerSubscriptionId,
          log.old_data.customerSubscriptionName,
        );

        changes.push(
          t`Na terminu za stranko ${log.old_data.customer.name} ${log.old_data.customer.lastName} je bila odstranjena uporaba paketa ${oldCustomerSubscriptionLabel}`,
        );
      }

      if (
        log.old_data.customerSubscriptionId != null &&
        log.new_data.customerSubscriptionId != null &&
        log.old_data.customerSubscriptionId !==
          log.new_data.customerSubscriptionId
      ) {
        const oldCustomerSubscriptionLabel = formatCustomerSubscriptionLabel(
          log.old_data.customerSubscriptionId,
          log.old_data.customerSubscriptionName,
        );
        const newCustomerSubscriptionLabel = formatCustomerSubscriptionLabel(
          log.new_data.customerSubscriptionId,
          log.new_data.customerSubscriptionName,
        );

        changes.push(
          t`Na terminu za stranko ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bila uporaba paketa spremenjena iz ${oldCustomerSubscriptionLabel} na ${newCustomerSubscriptionLabel}`,
        );
      }

      if (
        log.old_data.customerSubscriptionCreditId == null &&
        log.new_data.customerSubscriptionCreditId != null
      ) {
        const newCustomerSubscriptionCreditLabel =
          formatCustomerSubscriptionCreditLabel(
            log.new_data.customerSubscriptionCreditId,
          );

        changes.push(
          t`Na terminu za stranko ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bila označena uporaba kredita paketa ${newCustomerSubscriptionCreditLabel}`,
        );
      }

      if (
        log.old_data.customerSubscriptionCreditId != null &&
        log.new_data.customerSubscriptionCreditId == null
      ) {
        const oldCustomerSubscriptionCreditLabel =
          formatCustomerSubscriptionCreditLabel(
            log.old_data.customerSubscriptionCreditId,
          );

        changes.push(
          t`Na terminu za stranko ${log.old_data.customer.name} ${log.old_data.customer.lastName} je bila odstranjena uporaba kredita paketa ${oldCustomerSubscriptionCreditLabel}`,
        );
      }

      if (
        log.old_data.customerSubscriptionCreditId != null &&
        log.new_data.customerSubscriptionCreditId != null &&
        log.old_data.customerSubscriptionCreditId !==
          log.new_data.customerSubscriptionCreditId
      ) {
        const oldCustomerSubscriptionCreditLabel =
          formatCustomerSubscriptionCreditLabel(
            log.old_data.customerSubscriptionCreditId,
          );
        const newCustomerSubscriptionCreditLabel =
          formatCustomerSubscriptionCreditLabel(
            log.new_data.customerSubscriptionCreditId,
          );

        changes.push(
          t`Na terminu za stranko ${log.new_data.customer.name} ${log.new_data.customer.lastName} je bila uporaba kredita paketa spremenjena iz ${oldCustomerSubscriptionCreditLabel} na ${newCustomerSubscriptionCreditLabel}`,
        );
      }

      const disabledBefore = log.old_data.disabledNotificationSettingIds ?? [];
      const disabledAfter = log.new_data.disabledNotificationSettingIds ?? [];
      const turnedOff = disabledAfter.filter(
        (settingId) => !disabledBefore.includes(settingId),
      );
      const turnedOn = disabledBefore.filter(
        (settingId) => !disabledAfter.includes(settingId),
      );

      const notificationLabels = (settingIds: number[]) =>
        settingIds
          .map(
            (settingId) =>
              options?.notificationSettingLabelById?.[settingId] ||
              `#${settingId}`,
          )
          .join(", ");

      if (turnedOff.length > 0) {
        changes.push(
          t`Za ta termin so bila izklopljena obvestila: ${notificationLabels(turnedOff)}`,
        );
      }
      if (turnedOn.length > 0) {
        changes.push(
          t`Za ta termin so bila vklopljena obvestila: ${notificationLabels(turnedOn)}`,
        );
      }

      const oldContact = log.old_data.contactCustomer ?? null;
      const newContact = log.new_data.contactCustomer ?? null;
      const hasContactChanged =
        oldContact?.name !== newContact?.name ||
        oldContact?.lastName !== newContact?.lastName ||
        (oldContact == null) !== (newContact == null);

      if (includeContactCustomer && hasContactChanged) {
        if (!oldContact && newContact) {
          changes.push(
            t`Kontaktna stranka je bila nastavljena na ${newContact.name} ${newContact.lastName}`,
          );
        } else if (oldContact && !newContact) {
          changes.push(
            t`Kontaktna stranka ${oldContact.name} ${oldContact.lastName} je bila odstranjena`,
          );
        } else if (oldContact && newContact) {
          changes.push(
            t`Kontaktna stranka je bila spremenjena iz ${oldContact.name} ${oldContact.lastName} na ${newContact.name} ${newContact.lastName}`,
          );
        } else {
          changes.push(t`Kontaktna stranka je bila spremenjena`);
        }
      }
    }
  }

  if (log.table === "AppointmentResource") {
    if (log.action === "insert") {
      return t`Sredstva ${log.new_data.resourceLabels.join(" + ")} je bila dodana na termin.`;
    }
    if (log.action === "update") {
      if (log.new_data.resourceLabels.length > 0) {
        return t`Sredstva spremenjena iz ${log.old_data.resourceLabels.join(" + ")} na ${log.new_data.resourceLabels.join(" + ")}`;
      }
      return t`Vsa sredstva odstranjena s termina.`;
    }
    if (log.action === "delete") {
      return t`Sredstva ${log.old_data.resourceLabels.join(" + ")} so bila odstranjena s termina.`;
    }
  }

  if (log.table === "AppointmentUser") {
    if (log.action === "insert") {
      return t`Zaposleni so bili dodani na termin: ${log.new_data.users.map((user) => `${user.name} ${user.lastName}`).join(" + ")}.`;
    }
    if (log.action === "update") {
      return t`Zaposleni so bili spremenjeni iz ${log.old_data.users.map((user) => `${user.name} ${user.lastName}`).join(" + ")} na ${log.new_data.users.map((user) => `${user.name} ${user.lastName}`).join(" + ")}.`;
    }
    if (log.action === "delete") {
      return t`Zaposleni so bili odstranjeni s termina: ${log.old_data.users.map((user) => `${user.name} ${user.lastName}`).join(" + ")}.`;
    }
  }

  return changes.length > 0 ? changes.join(". ") + "." : "";
};

export function getPaymentStatusText(status: string | null): string {
  switch (status) {
    case "canceled":
      return t`Preklicano`;
    case "requires_payment_method":
      return t`Plačilo zahtevano`;
    case "refunded":
      return t`Povrnjeno`;
    case "succeeded":
      return t`Plačilo uspešno`;
    default:
      return t`Neznan status`;
  }
}

export function getAttendanceStatusText(customerDidAttend: number): string {
  switch (customerDidAttend) {
    case 1:
      return t`Prisoten`;
    case -1:
      return t`Neprihod`;
    case 0:
      return t`Neoznačeno`;
    default:
      return t`Neznan status`;
  }
}

function normalizeAppointmentComment(comment: string | null) {
  return comment ?? "";
}

function formatCustomerSubscriptionLabel(
  customerSubscriptionId: number,
  customerSubscriptionName: null | string | undefined,
) {
  if (!customerSubscriptionName) {
    return `#${customerSubscriptionId}`;
  }

  return `#${customerSubscriptionId} (${customerSubscriptionName})`;
}

function formatCustomerSubscriptionCreditLabel(
  customerSubscriptionCreditId: number,
) {
  return `#${customerSubscriptionCreditId}`;
}

export const generateAdditionalInfo = (
  log: GetAppointmentLog["response"]["logs"][0],
) => {
  return log.user?.name
    ? `${log.timestampLocalized} ${log.user.name} ${log.user.lastName}`
    : t`${log.timestampLocalized} stranka`;
};
